import { FC, useEffect, useState } from 'react'
import { AppMarkdownViewer } from '../../../atoms/AppMarkdownViewer/AppMarkdownViewer'
import HelpTooltip from '../../HelpTooltip'
import { useSelector } from 'react-redux'
import { RootState } from '../../../..'
import { Employee } from '../../../../api/openapi'
import { RadioButton } from 'primereact/radiobutton'
import { classNames } from 'primereact/utils'
import { AnswerSkipReasonEnum } from '../../../../types/enums/answer-skip-reason.enum'

interface SelectQuestionProps {
  index: number
  max: number
  min: number
  value?: number
  title: string
  description?: string
  minLabel: string
  maxLabel: string
  unfinished?: boolean
  required?: boolean
  skippable?: string
  skipReason?: AnswerSkipReasonEnum
  tutorialButton?: boolean
  onChange?: (value?: number, skipReason?: AnswerSkipReasonEnum) => void
}

export const SelectQuestion: FC<SelectQuestionProps> = ({
  index,
  max,
  min,
  title,
  description,
  minLabel,
  maxLabel,
  value = undefined,
  unfinished = false,
  required = true,
  skippable,
  skipReason,
  tutorialButton = false,
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState<number | undefined>(value)
  const [selectedSkipReason, setSelectedSkipReason] = useState<AnswerSkipReasonEnum | undefined>(skipReason)

  const currentUser = useSelector<RootState, Employee>(state => state.auth.user as Employee)
  const isUser = currentUser.role === 'user'

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  useEffect(() => {
    setSelectedSkipReason(skipReason)
  }, [skipReason])

  const handleValueSelected = (selectedValue: number): void => {
    onChange?.(selectedValue)
    setSelectedValue(selectedValue)
    setSelectedSkipReason(undefined)
  }

  const handleSkipQuestion = (reason: AnswerSkipReasonEnum): void => {
    onChange?.(undefined, selectedSkipReason === reason ? undefined : reason)
    setSelectedValue(undefined)
    setSelectedSkipReason(selectedSkipReason === reason ? undefined : reason)
  }

  const renderRange = () => {
    const range = max - min + 1
    return Array.from({ length: range }, (_, i) => {
      const value = min + i
      const isMin = value === min
      const isMax = value === max
      return (
        <div key={value} className="flex items-center">
          {isMin && <span className="text-xl font-semibold">{minLabel}</span>}
          <div className="flex flex-col w-4 gap-y-2 mx-8 items-center">
            <RadioButton
              className="assessment-radio-button"
              value={value}
              onChange={() => handleValueSelected(value)}
              checked={selectedValue === value}
            />
          </div>
          {isMax && <span className="text-xl font-semibold mr-6">{maxLabel}</span>}
        </div>
      )
    })
  }

  const questionBody = (
    <div
      className={classNames(
        'select-question w-full flex min-h-[13rem] rounded-[0.8rem] bg-pale-cyan py-[1.6rem] px-[2.2rem] gap-4',
        {
          'bg-pale-yellow': required && !!skippable && !selectedSkipReason && !value && unfinished,
        }
      )}
    >
      <span className="text-dark-blue font-extrabold text-[1.6rem] leading-[2.2rem]">{index}</span>
      <div className="flex flex-col gap-3.5 w-full">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex gap-4 leading-[2.2rem] font-normal text-[1.6rem]">
            <AppMarkdownViewer markdown={title} />
            {skippable && <p className="text-[10px] font-semibold text-stone-gray">Optional</p>}
          </div>
          {description && (
            <>
              <AppMarkdownViewer
                className="text-stone-gray font-normal text-[1.6rem] leading-[2.2rem]"
                markdown={description}
              />
            </>
          )}
        </div>
        <div className="w-fit flex flex-row gap-8">
          <div className="inline">
            <div className="flex h-[50px] my-8 align-middle items-center gap-4">
              {renderRange()}
              {skippable && (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-px bg-toast-hover/60" aria-hidden="true" />
                  <button
                    className={classNames(
                      'self-center border-solid border-[0.5px] box-border rounded-[4px] h-8 px-4 cursor-pointer text-xl duration-[200ms] whitespace-nowrap',
                      {
                        'bg-white border-toast-hover unfinished:bg-pale-yellow hover:border-orange hover:text-white hover:bg-orange':
                          selectedSkipReason !== AnswerSkipReasonEnum.NOT_APPLICABLE,
                        'border-orange text-white bg-orange':
                          selectedSkipReason === AnswerSkipReasonEnum.NOT_APPLICABLE,
                      }
                    )}
                    onClick={() => handleSkipQuestion(AnswerSkipReasonEnum.NOT_APPLICABLE)}
                  >
                    Not applicable
                  </button>
                  <button
                    className={classNames(
                      'self-center border-solid border-[0.5px] box-border rounded-[4px] h-8 px-4 cursor-pointer text-xl duration-[200ms] whitespace-nowrap',
                      {
                        'bg-white border-toast-hover unfinished:bg-pale-yellow hover:border-orange hover:text-white hover:bg-orange':
                          selectedSkipReason !== AnswerSkipReasonEnum.NO_VISIBILITY,
                        'border-orange text-white bg-orange': selectedSkipReason === AnswerSkipReasonEnum.NO_VISIBILITY,
                      }
                    )}
                    onClick={() => handleSkipQuestion(AnswerSkipReasonEnum.NO_VISIBILITY)}
                  >
                    No visibility
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return tutorialButton ? (
    <div
      className={`w-full flex min-h-[13rem] border-[0.8rem] bg-pale-cyan py-[1.6rem] px-[2rem] gap-4 !p-0 ${
        required && skippable && !selectedSkipReason && !value && unfinished ? 'bg-pale-yellow' : ''
      }`}
    >
      <HelpTooltip
        id={isUser ? 'user-assesment-tutorial-step-3' : 'review-tutorial-step-5'}
        childrenClassNames="relative z-50 bg-white flex flex-wrap w-fit items-center flex-1"
      >
        {questionBody}
      </HelpTooltip>
    </div>
  ) : (
    questionBody
  )
}
